import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectClients, openIntercomChat, selectSessionAdvisor } from '@client';
import { useCancellationSituation } from './use-cancellation-situation';
import { CANCELLATION_FLOWS } from '../lib/cancellation-flows';
import { useInviteClientStep } from '@client/features/advisor-onboarding';

interface UseCancellationModalProps {
  onClose: () => void;
}

export function useCancellationModal({ onClose }: UseCancellationModalProps) {
  const navigate = useNavigate();
  const advisor = useSelector(selectSessionAdvisor);
  const advisorId = advisor.advisorId;
  const { data: situationData, isLoading } = useCancellationSituation();
  const clients = useSelector(selectClients);
  const { handleQuickPlanSuccess } = useInviteClientStep();
  const [isSendResultsModalOpen, setIsSendResultsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isQuickPlanModalOpen, setIsQuickPlanModalOpen] = useState(false);

  const situation: SubscriptionCancellationSituation = situationData?.situation || 'low-engagement';
  const flowConfig = CANCELLATION_FLOWS[situation];

  const handleOpenFeedbackModal = useCallback(() => {
    onClose(); // Close the cancellation modal
    setIsFeedbackModalOpen(true);
  }, [onClose]);

  const handleCloseFeedbackModal = useCallback(() => {
    setIsFeedbackModalOpen(false);
  }, []);

  const handleCloseSendResultsModal = useCallback(() => {
    setIsSendResultsModalOpen(false);
    setSelectedClient(null);
    navigate(`/advisors/${advisorId}/clients`);
  }, [advisorId, navigate]);

  const handleCloseQuickPlanModal = useCallback(() => {
    setIsQuickPlanModalOpen(false);
  }, []);

  const handleQuickPlanSuccessModal = useCallback(() => {
    handleQuickPlanSuccess();
    setIsQuickPlanModalOpen(false);
    navigate(`/advisors/${advisorId}/clients`);
  }, [advisorId, navigate]);

  const handlePrimaryAction = useCallback(() => {
    switch (situation) {
      case 'no-plans-created':
        setIsQuickPlanModalOpen(true);
        onClose();
        break;
      case 'only-self-plan':
      case 'low-engagement':
        navigate(`/advisors/${advisorId}/clients?inviteClient=true&inviteClientType=email`);
        onClose();
        break;
      case 'created-not-reviewed':
        clients.length > 0
          ? navigate(`/clients/${clients[0].clientId}/onboarding/overview`)
          : navigate(`/advisors/${advisorId}/clients`);
        onClose();
        break;
      case 'reviewed-not-shared':
        const clientWithEmail = clients.find(
          client => client.clientEmail && client.clientEmail !== advisor.advisorEmail,
        );
        if (clientWithEmail) {
          setSelectedClient(clientWithEmail);
          setIsSendResultsModalOpen(true);
        } else {
          navigate(`/advisors/${advisorId}/clients`);
        }
        onClose();
        break;
      case 'high-engagement':
        openIntercomChat();
        break;
      default:
        break;
    }
  }, [situation, advisorId, clients, navigate, onClose]);

  return {
    isLoading,
    flowConfig,
    isSendResultsModalOpen,
    selectedClient,
    handleCloseSendResultsModal,
    handlePrimaryAction,
    isFeedbackModalOpen,
    handleOpenFeedbackModal,
    handleCloseFeedbackModal,
    isQuickPlanModalOpen,
    handleCloseQuickPlanModal,
    handleQuickPlanSuccessModal,
    advisorId,
  };
}
